import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { SecondaryButtonMedium } from '@/components/base/Buttons';
import { TypographyH6, TypographySmall } from '@/components/base/Typography';
import { DiamondIcon, SettingIcon, RingIcon } from '@/components/diamonds/DesktopProcessStep';
import { Dialog, DialogContent, DialogFooter, DialogHeader } from '@/components/ui/dialog';
import { PROCESS_ORDERS } from '@/constants';
import { getCart } from '@/utils/cart';
import { generateDiamondSlug, generateSettingSlug } from '@/utils/slug';

const MobileStep = ({ number, title, image, icon, href, viewHref, itemId }) => {
  const router = useRouter();
  const [open, setOpen] = useState(false);

  // Handle click on a step
  const handleClick = () => {
    if (title === 'Complete') {
      if (href) router.push(href);
      return;
    }

    if (!itemId) {
      // Nothing selected yet – just go to the browse page
      if (href) router.push(href);
    } else {
      // Ask the user what they want to do
      setOpen(true);
    }
  };

  const handleBrowse = () => {
    setOpen(false);
    if (href) router.push(href);
  };

  const handleView = () => {
    setOpen(false);
    if (viewHref) router.push(viewHref);
  };

  return (
    <>
      <button onClick={handleClick} className="flex flex-1 flex-col items-center space-y-1">
        <div className="flex h-12 w-12 items-center justify-center">
          {image ? (
            <img src={image} alt={title} className="h-12 w-12 rounded-sm object-cover" />
          ) : (
            icon
          )}
        </div>
        <TypographySmall>
          {number}. {title}
        </TypographySmall>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="space-y-4">
          <DialogHeader>
            <TypographyH6>What would you like to do?</TypographyH6>
          </DialogHeader>
          <DialogFooter className="flex-col space-y-4 sm:flex-col">
            {viewHref && (
              <SecondaryButtonMedium className="w-full" size="lg" onClick={handleView}>
                View Selected {title}
              </SecondaryButtonMedium>
            )}
            <SecondaryButtonMedium
              variant="outline"
              size="lg"
              className="w-full"
              onClick={handleBrowse}
            >
              Browse {title === 'Diamond' ? 'Diamonds' : 'Settings'}
            </SecondaryButtonMedium>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const MobileProcessSteps = ({ currentStep: propCurrent }) => {
  const router = useRouter();

  // Determine current step if not provided
  let currentStep = propCurrent;
  if (!currentStep) {
    const path = router.pathname;
    if (path.startsWith('/diamond') || path.startsWith('/diamonds')) currentStep = 'diamond';
    else if (path.startsWith('/ring') || path.startsWith('/rings')) currentStep = 'rings';
    else if (path.startsWith('/cart')) currentStep = 'ring';
  }

  // Read cart items (client-side only)
  const cart = typeof window !== 'undefined' ? getCart() : [];
  const diamondItems = cart.filter((i) => i.itemType !== 'ring');
  const ringItems = cart.filter((i) => i.itemType === 'ring');
  const firstDiamond = diamondItems.sort((a, b) => new Date(a.addedAt) - new Date(b.addedAt))[0];
  const firstRing = ringItems.sort((a, b) => new Date(a.addedAt) - new Date(b.addedAt))[0];

  // Determine ordering of diamond vs setting based on cart
  let order;
  if (firstDiamond && firstRing) {
    order =
      new Date(firstDiamond.addedAt) < new Date(firstRing.addedAt)
        ? PROCESS_ORDERS.DIAMOND_FIRST
        : PROCESS_ORDERS.RING_FIRST;
  } else if (firstDiamond) {
    order = PROCESS_ORDERS.DIAMOND_FIRST;
  } else if (firstRing) {
    order = PROCESS_ORDERS.RING_FIRST;
  } else {
    order = currentStep === 'rings' ? PROCESS_ORDERS.RING_FIRST : PROCESS_ORDERS.DIAMOND_FIRST;
  }

  const steps = order.map((type, idx) => {
    const isDiamond = type === 'diamond';
    const item = isDiamond ? firstDiamond : firstRing;

    const defaultTitle = isDiamond ? 'Diamond' : 'Setting';

    // Determine href/viewHref
    let viewHref;
    if (item) {
      if (isDiamond) {
        const basePath = item.labgrown ? '/lab-diamond' : '/diamond';
        const shapeSlug = (item.shape || '').toLowerCase();
        const slug = generateDiamondSlug({
          diamondId: item.diamondId,
          shape: item.shape,
          carats: item.carats,
          col: item.color,
          clar: item.clarity,
          cut: item.cut,
          lab: item.lab,
          labgrownType: item.labgrown ? 'lab' : undefined,
        });
        viewHref = `${basePath}/${shapeSlug}/${slug}`;
      } else {
        const numericId = (item.productId || item.diamondId || '').split('/').pop();
        const slug = generateSettingSlug({ id: numericId, name: item.productName || '' });
        viewHref = `/ring/${slug}`;
      }
    }

    let cardHref;
    if (isDiamond) {
      cardHref = item ? (item.labgrown ? '/lab-diamonds' : '/diamonds') : '/diamonds';
    } else {
      cardHref = '/rings';
    }

    return {
      number: (idx + 1).toString(),
      title: defaultTitle,
      icon: isDiamond ? <DiamondIcon className="h-6 w-6" /> : <SettingIcon className="h-6 w-6" />,
      image: item?.image,
      href: cardHref,
      viewHref,
      itemId: item?.diamondId,
    };
  });

  // Third step – Complete/Ring
  const showRingDetails = firstDiamond && firstRing;
  steps.push({
    number: '3',
    title: 'Complete',
    icon: <RingIcon className="h-6 w-6" />,
    href: showRingDetails ? '/cart' : undefined,
    image: showRingDetails ? firstRing?.image : undefined,
    itemId: showRingDetails ? firstRing?.diamondId : undefined,
  });

  return (
    <div className="relative right-1/2 left-1/2 -mr-[50vw] -ml-[50vw] flex w-screen items-center border-b bg-white pb-2">
      {steps.map((step, idx) => (
        <React.Fragment key={step.number}>
          {/* Individual step */}
          <MobileStep key={step.number} {...step} />

          {/* Right-pointing triangle between steps */}
          {idx < steps.length - 1 && (
            <span
              key={`arrow-${idx}`}
              className="border-l-muted-foreground mx-2 block h-0 w-0 border-y-4 border-l-8 border-y-transparent"
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
